import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './Reports.css';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// Turn an array of objects into a CSV download
function downloadCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function Reports({ embedded = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API_BASE_URL}/api/reports/overview?start=${start}&end=${end}`);
      setData(res.data);
    } catch (err) {
      toast.error('Could not load reports');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) return <div className="rp-wrap"><div className="rp-loading">Loading reports…</div></div>;

  const { revenue, aging, debtors, enrollment, attendance } = data;
  const agingTotal = aging.b0_30 + aging.b31_60 + aging.b61_90 + aging.b90_plus;
  const agingBuckets = [
    { label: '0–30 days', v: aging.b0_30, cls: 'ok' },
    { label: '31–60 days', v: aging.b31_60, cls: 'warn' },
    { label: '61–90 days', v: aging.b61_90, cls: 'bad' },
    { label: '90+ days', v: aging.b90_plus, cls: 'bad' },
  ];

  return (
    <div className="rp-wrap">
      <div className="rp-head">
        {!embedded && <h1>💰 Business Reports</h1>}
        <div className="rp-period">
          <label>From <input type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
          <label>To <input type="date" value={end} onChange={e => setEnd(e.target.value)} /></label>
          <button className="rp-btn" onClick={load}>Apply</button>
          <button className="rp-btn ghost" onClick={() => navigate('/billing?tab=accounting')}>Expenses / P&amp;L →</button>
        </div>
      </div>

      {/* Revenue tiles */}
      <div className="rp-tiles">
        <div className="rp-tile good"><span className="rp-tile-v">{money(revenue.collected)}</span><span className="rp-tile-l">Collected (period) · {revenue.paid_count} paid</span></div>
        <div className={`rp-tile ${revenue.outstanding > 0 ? 'bad' : 'good'}`}><span className="rp-tile-v">{money(revenue.outstanding)}</span><span className="rp-tile-l">Outstanding · {revenue.open_invoices} open</span></div>
        <div className="rp-tile neutral"><span className="rp-tile-v">{enrollment.reduce((s, e) => s + e.active, 0)}</span><span className="rp-tile-l">Active enrollments</span></div>
      </div>

      {/* A/R aging */}
      <div className="rp-card">
        <div className="rp-card-head">
          <h2>📉 Accounts Receivable — aging</h2>
          <button className="rp-export" onClick={() => downloadCsv('ar-aging.csv', ['Bucket', 'Amount'], agingBuckets.map(b => [b.label, b.v.toFixed(2)]))}>⬇ CSV</button>
        </div>
        <div className="rp-aging">
          {agingBuckets.map(b => (
            <div key={b.label} className="rp-aging-row">
              <span className="rp-aging-label">{b.label}</span>
              <div className="rp-aging-bar"><div className={`rp-aging-fill ${b.cls}`} style={{ width: `${agingTotal ? (b.v / agingTotal) * 100 : 0}%` }} /></div>
              <span className="rp-aging-amt">{money(b.v)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top debtors */}
      <div className="rp-card">
        <div className="rp-card-head">
          <h2>🧾 Who owes (top {debtors.length})</h2>
          <button className="rp-export" onClick={() => downloadCsv('outstanding-by-athlete.csv', ['Athlete', 'Outstanding', 'Oldest (days)'], debtors.map(d => [d.name, d.outstanding.toFixed(2), d.oldest_days]))}>⬇ CSV</button>
        </div>
        {debtors.length === 0 ? <p className="rp-empty">Nobody owes — all paid up. 🎉</p> : (
          <table className="rp-table">
            <thead><tr><th>Athlete</th><th>Outstanding</th><th>Oldest</th><th></th></tr></thead>
            <tbody>
              {debtors.map(d => (
                <tr key={d.athlete_id}>
                  <td className="rp-link" onClick={() => navigate(`/athlete-card/${d.athlete_id}`)}>{d.name}</td>
                  <td className="rp-due">{money(d.outstanding)}</td>
                  <td>{d.oldest_days > 0 ? `${d.oldest_days}d` : '—'}</td>
                  <td><button className="rp-mini" onClick={() => navigate(`/athlete-card/${d.athlete_id}`)}>Card</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Enrollment by class */}
      <div className="rp-card">
        <div className="rp-card-head">
          <h2>📚 Enrollment by class</h2>
          <button className="rp-export" onClick={() => downloadCsv('enrollment.csv', ['Class', 'Active', 'Capacity'], enrollment.map(e => [e.class_name, e.active, e.capacity ?? '']))}>⬇ CSV</button>
        </div>
        {enrollment.length === 0 ? <p className="rp-empty">No classes.</p> : (
          <table className="rp-table">
            <thead><tr><th>Class</th><th>Active</th><th>Capacity</th><th>Fill</th></tr></thead>
            <tbody>
              {enrollment.map(e => (
                <tr key={e.class_id}>
                  <td>{e.class_name}</td>
                  <td>{e.active}</td>
                  <td>{e.capacity ?? '—'}</td>
                  <td>{e.capacity ? `${Math.round((e.active / e.capacity) * 100)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Attendance by class */}
      <div className="rp-card">
        <div className="rp-card-head">
          <h2>✅ Attendance by class <small>({start} → {end})</small></h2>
          <button className="rp-export" onClick={() => downloadCsv('attendance.csv', ['Class', 'Marked', 'Present', 'Absent', 'Late', 'Rate %'], attendance.map(a => [a.class_name, a.marked, a.present, a.absent, a.late, a.rate ?? '']))}>⬇ CSV</button>
        </div>
        {attendance.length === 0 ? <p className="rp-empty">No attendance recorded in this period.</p> : (
          <table className="rp-table">
            <thead><tr><th>Class</th><th>Marked</th><th>Present</th><th>Absent</th><th>Late</th><th>Rate</th></tr></thead>
            <tbody>
              {attendance.map(a => (
                <tr key={a.class_id}>
                  <td>{a.class_name}</td>
                  <td>{a.marked}</td>
                  <td>{a.present}</td>
                  <td>{a.absent}</td>
                  <td>{a.late}</td>
                  <td><span className={`rp-rate ${a.rate >= 80 ? 'good' : a.rate >= 60 ? 'warn' : 'bad'}`}>{a.rate != null ? `${a.rate}%` : '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Reports;
